import { Observable, BehaviorSubject, of as observableOf, of } from 'rxjs';
import { mergeMap, catchError, share } from "rxjs/operators";
import { AuthenticationService } from '../AuthenticationService/authentication.service';
import { addProvider, IProviderDescriptor } from "../ServiceUtil";
import { PropertyDelegate } from './property.delegate';
import { INewProperty,
 INewPropertyContractDocument,
 INewPropertyRentDocument, 
IProperty, 
IPropertyResponse, 
IPropertySuite } from './property.interface';
 
export class PropertyService
{

    private propertiesData: IProperty[] = null;
    private propertiesSubject: BehaviorSubject<IProperty[]> = new BehaviorSubject(null);
    public properties: Observable<IProperty[]> = this.propertiesSubject;

    private propertiesFilteredSubject: BehaviorSubject<IProperty[]> = new BehaviorSubject(null);
    public propertiesFiltered: Observable<IProperty[]> = this.propertiesFilteredSubject;

    private selectedPropertyId: string = "0";
    private selectedPropertySuiteId: string = "0";

    private selectedPropertyData: IProperty = null;
    private selectedPropertySubject: BehaviorSubject<IProperty> = new BehaviorSubject(this.selectedPropertyData);
    public selectedProperty: Observable<IProperty> = this.selectedPropertySubject;


    private propertySuiteData: IPropertySuite[] = null;
    private propertySuiteSubject: BehaviorSubject<IPropertySuite[]> = new BehaviorSubject(null);
    public propertySuites: Observable<IPropertySuite[]> = this.propertySuiteSubject;

    private selectedPropertySuiteData: IPropertySuite = null;
    private selectedPropertySuiteSubject: BehaviorSubject<IPropertySuite> = new BehaviorSubject(this.selectedPropertySuiteData);
    public selectedPropertySuite: Observable<IPropertySuite> = this.selectedPropertySuiteSubject;

    private propertySuiteFilteredSubject: BehaviorSubject<IPropertySuite[]> = new BehaviorSubject(null);
    public propertySuiteFiltered: Observable<IPropertySuite[]> = this.propertySuiteFilteredSubject;

    constructor(private propertyDelegate: PropertyDelegate, private authenticationService: AuthenticationService
)
    {
    }

    public get propertyId(): string
    {
        return this.selectedPropertyId;
    }

    public setSelectedPropertyId(propertyId: string): void
    {
        this.selectedPropertyId = propertyId;
    }

    public get propertySuiteId(): string
    {
        return this.selectedPropertySuiteId;
    }

    public setSelectedPropertySuiteId(propertySuiteId: string): void
    {
        this.selectedPropertySuiteId = propertySuiteId;
    }

    public getSelectedPropertyById(propertyId: string): Observable<boolean>
    {
        if (propertyId === "0")
        {
            this.selectedPropertyData = null;
            this.selectedPropertySubject.next(this.selectedPropertyData);
            return observableOf(false);
        }

        var response = this.propertyDelegate.getPropertyById(propertyId);

        return this.processProperty(response);

    }

    public getSelectedPropertySuiteById(propertySuiteId: string): Observable<boolean>
    {
        if (propertySuiteId === "0")
        {
            this.selectedPropertySuiteData = null;
            this.selectedPropertySuiteSubject.next(this.selectedPropertySuiteData);
            return observableOf(false);
        }

        var response = this.propertyDelegate.getPropertySuiteById(propertySuiteId);

        return this.processPropertySuite(response);

    }
    public getAllProperties(): Observable<boolean>
    {
        if (this.propertiesData !== null && this.propertiesData.length > 0)
        {
            return of(true);
        }
        const orgId = this.authenticationService.GetOrgId();

        const response = this.propertyDelegate.getAllProperties(orgId);
        return this.processProperties(response);
    }

    public createNewContractDocuments(documents: INewPropertyContractDocument): Observable<boolean>
    {
        const response = this.propertyDelegate.createPropertyContracts(documents);
        return this.processProperty(response);
    }


    public createNewRentDocuments(documents: INewPropertyRentDocument): Observable<boolean>
    {
        const response = this.propertyDelegate.createPropertyRentContracts(documents);
        return this.processProperty(response);
    }

    public createNewProperty(property: INewProperty): Observable<boolean>
    {
        const orgId = this.authenticationService.GetOrgId();
        property.orgId = orgId;
        property.clientTimeStamp = new Date().toISOString();
        const response = this.propertyDelegate.createNewProperty(property);
        return this.processProperties(response);
    }

    public updateProperty(property: IProperty): Observable<boolean>
    {
        property.clientTimeStamp = new Date().toISOString();
        const response = this.propertyDelegate.updateProperty(property);
        return this.processProperties(response);
    }

    public getAllPropertySuites(): Observable<boolean>
    {
        if (this.propertySuiteData !== null && this.propertySuiteData.length > 0)
        {
            return of(true);
        }
        const orgId = this.authenticationService.GetOrgId();

        const response = this.propertyDelegate.getAllPropertySuites(orgId);
        return this.processPropertySuites(response);
    }

    public createNewPropertySuite(propertySuite: IPropertySuite): Observable<boolean>
    {
        propertySuite.clientTimeStamp = new Date().toISOString();
        const response = this.propertyDelegate.createPropertySuite(propertySuite);
        return this.processProperty(response);
    }

    public updatePropertySuite(propertySuite: IPropertySuite): Observable<boolean>
    {
        propertySuite.clientTimeStamp = new Date().toISOString();

        const response = this.propertyDelegate.updatePropertySuite(propertySuite);
        return this.processProperty(response);
    }

    private processPropertySuites(properties: Observable<IPropertyResponse>): Observable<boolean>
    {
        properties.subscribe(onSuccess.bind(this), onFault.bind(this));

        return properties.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(propertyResponse: IPropertyResponse)
        {
            if (propertyResponse.success && propertyResponse.propertySuites.length > 0)
            {
                this.selectedPropertyId = propertyResponse.propertyId ? propertyResponse.propertyId : this.selectedPropertyId;
                this.propertySuiteData = propertyResponse.propertySuites;
                this.propertySuiteSubject.next(this.propertySuiteData);
                this.propertySuiteFilteredSubject.next(this.propertySuiteData);
            }
        }

        function onFault(error: any)
        {
            //PropertyService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processProperties(properties: Observable<IPropertyResponse>): Observable<boolean>
    {
        properties.subscribe(onSuccess.bind(this), onFault.bind(this));

        return properties.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(propertyResponse: IPropertyResponse)
        {
            if (propertyResponse.success && propertyResponse.properties.length > 0)
            {
                this.selectedPropertyId = propertyResponse.propertyId ? propertyResponse.propertyId : this.selectedPropertyId;
                this.propertiesData = propertyResponse.properties;
                this.propertiesSubject.next(this.propertiesData);
                this.propertiesFilteredSubject.next(this.propertiesData);
            }
        }

        function onFault(error: any)
        {
             //PropertyService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processProperty(propertyResponse: Observable<IPropertyResponse>): Observable<boolean>
    {
        propertyResponse.subscribe(onSuccess.bind(this), onFault.bind(this));

        return propertyResponse.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(propertyResponse: IPropertyResponse)
        {
            if (propertyResponse.success && propertyResponse.property)
            {
                this.selectedPropertyId = propertyResponse.propertyId ? propertyResponse.propertyId : this.selectedPropertyId;
                this.selectedPropertyData = propertyResponse.property;
                this.selectedPropertySubject.next(this.selectedPropertyData);


            }
        }

        function onFault(error: any)
        {
            // VendorService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }

    private processPropertySuite(propertyResponse: Observable<IPropertyResponse>): Observable<boolean>
    {
        propertyResponse.subscribe(onSuccess.bind(this), onFault.bind(this));

        return propertyResponse.pipe(mergeMap((response: any) => !response ? observableOf(false) : observableOf(true)),
            catchError(() => observableOf(false)),
            share());

        function onSuccess(propertyResponse: IPropertyResponse)
        {
            if (propertyResponse.success && propertyResponse.propertySuite)
            {
                this.selectedPropertySuiteId = propertyResponse.propertySuiteId ? propertyResponse.propertySuiteId : this.selectedPropertySuiteId;
                this.selectedPropertySuiteData = propertyResponse.propertySuite;
                this.selectedPropertySuiteSubject.next(this.selectedPropertySuiteData);


            }
        }

        function onFault(error: any)
        {
            // VendorService.logger.error(`processVendors Fault - Error (${JSON.stringify(error)})`);
        }
    }
}